/** @file Segment ends, rig curve codes, control points and values of a curve track (core evalCurve). */
import { compileCurve, type CurveCode } from '@pose2d/compiler/compile/compileCurve';
import { evalCurve } from '@pose2d/core/curve/evalCurve';
import { findSegment } from '@pose2d/core/curve/findSegment';
import type { CurveTrack } from './curveTypes';

/** One-segment curve table handed to evalCurve, so the graph evaluates exactly what the runtimes play. */
const kind = new Uint8Array(1);
const cp = new Float64Array(4);

function load(code: CurveCode): void {
  if (typeof code === 'number') {
    kind[0] = code;
    return;
  }
  kind[0] = 2;
  cp.set(code);
}

/** Segment math of a track; segment i runs from key i to key i + 1 and takes key i's curve. */
export const curveSegment = {
  /** Start and end value of segment i (0 and 1 on progress tracks). */
  ends(track: CurveTrack, i: number): [number, number] {
    if (track.mode === 'progress') return [0, 1];
    const v0 = track.values[i] ?? 0;
    return [v0, track.values[i + 1] ?? v0];
  },
  /** Rig curve code of segment i (§5.4): 0 linear, 1 stepped, or [x1, cy1, x2, cy2] with x clamped, cy absolute. */
  code(track: CurveTrack, i: number): CurveCode {
    const [v0, v1] = curveSegment.ends(track, i);
    return compileCurve(track.curves[i], v0, v1);
  },
  /** Bezier control points [x1, cy1, x2, cy2] of segment i; linear and stepped give the straight-line thirds. */
  controls(track: CurveTrack, i: number): [number, number, number, number] {
    const code = curveSegment.code(track, i);
    if (typeof code !== 'number') return [code[0], code[1], code[2], code[3]];
    const [v0, v1] = curveSegment.ends(track, i);
    return [1 / 3, v0 + (v1 - v0) / 3, 2 / 3, v0 + (2 * (v1 - v0)) / 3];
  },
  /** Value of segment i at normalized time u in [0, 1]; stepped holds the start value until the next key. */
  valueAt(track: CurveTrack, i: number, u: number): number {
    const [v0, v1] = curveSegment.ends(track, i);
    load(curveSegment.code(track, i));
    return evalCurve(kind, cp, 0, v0, v1, u);
  },
  /** Value at a frame: the first key's before the keys, the last key's after them. */
  frameValue(track: CurveTrack, frame: number): number {
    const last = track.frames.length - 1;
    const i = findSegment(Float64Array.from(track.frames), frame);
    if (i < 0 || i >= last) return track.values[Math.max(0, Math.min(i, last))] ?? 0;
    const f0 = track.frames[i] ?? 0;
    const f1 = track.frames[i + 1] ?? f0 + 1;
    return curveSegment.valueAt(track, i, (frame - f0) / (f1 - f0));
  },
};
